// @l-doc: doc/模板sku方案设计/modules/editImageNew/绘制文字工具字体流程.md
import Toast from '@/components/Toast';
import * as constant from '@/utils/constant.js';
import * as mwx from '@/utils/common.js';
import { imageDataRenderUrlCache } from '@/utils/editImage/imageDataRenderCache.js';
import { getImageDataLayerRenderSize } from '@/utils/editImage/imageDataRenderSizeUtil.js';
import {
  ensureImageDataFontsLoaded,
  IMAGE_DATA_FONT_NOT_FOUND_MESSAGE,
  resolveImageDataTextFontFamily
} from '@/utils/editImage/imageDataFontUtil.js';
import {
  Group,
  IText,
  Image as FabricImage,
  Point,
  StaticCanvas,
  util
} from 'fabric';

const IMAGE_DATA_TEXT_OBJECT_TYPE = 'IText';

/**
 * 创建离屏 StaticCanvas。
 * @param {Object} options - 离屏画布配置。
 * @param {number} options.width - 画布宽度。
 * @param {number} options.height - 画布高度。
 * @returns {fabric.StaticCanvas} 离屏 Fabric 画布。
 */
function createOffscreenStaticCanvas(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('createOffscreenStaticCanvas: width/height 无效');
  }

  if (typeof document === 'undefined') {
    throw new Error('createOffscreenStaticCanvas: document 不存在');
  }

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width;
  canvasEl.height = height;

  const offscreenCanvas = new StaticCanvas(canvasEl, {
    width: width,
    height: height,
    enableRetinaScaling: false
  });

  // 避免 add/remove 时频繁重绘，最终由调用方统一 renderAll/toDataURL。
  offscreenCanvas.renderOnAddRemove = false;
  return offscreenCanvas;
}

/**
 * 更新离屏 StaticCanvas 的画布宽度。
 * @param {fabric.StaticCanvas} offscreenCanvas - 离屏 Fabric 画布。
 * @param {number} width - 新画布宽度。
 * @param {number} height - 画布高度。
 * @returns {void}
 */
function setOffscreenStaticCanvasSize(offscreenCanvas, width, height) {
  // Fabric 实例缺失时无法调整画布尺寸。
  if (!offscreenCanvas) {
    return;
  }
  // Fabric 提供 setDimensions 时优先使用官方 API，同步元素和实例尺寸。
  if (typeof offscreenCanvas.setDimensions === 'function') {
    offscreenCanvas.setDimensions({ width, height });
    return;
  }
  const canvasEl = getNativeCanvasFromStaticCanvas(offscreenCanvas);
  // 原生 canvas 存在时同步更新真实像素尺寸。
  if (canvasEl) {
    canvasEl.width = width;
    canvasEl.height = height;
  }
  offscreenCanvas.width = width;
  offscreenCanvas.height = height;
}

/**
 * 将任意值转换为有限数字。
 * @param {*} value - 需要转换的值。
 * @param {number} fallback - 转换失败时的兜底值。
 * @returns {number} 转换后的数字。
 */
function numberOr(value, fallback) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

/**
 * 将数值限制在指定区间内。
 * @param {number} value - 原始数值。
 * @param {number} min - 最小值。
 * @param {number} max - 最大值。
 * @returns {number} 限制后的数值。
 */
function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, numberOr(value, min)));
}

/**
 * 稳定序列化对象，避免字段顺序变化导致同一 imageData 生成不同 key。
 * @param {*} value - 需要序列化的值。
 * @returns {string} 稳定序列化后的字符串。
 */
function stableStringify(value) {
  // 数组按原顺序序列化，保留 imageData 内对象层级关系。
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  // 普通对象按 key 排序后序列化，避免对象属性顺序影响缓存 key。
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * 生成短 hash，避免直接把完整 imageData 字符串作为缓存 key。
 * @param {string} value - 需要生成 hash 的字符串。
 * @returns {string} hash 字符串。
 */
function hashString(value) {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

/**
 * 根据解析后的 imageData 生成渲染缓存 key。
 * @param {Object} imageData - 解析后的 imageData。
 * @param {Object} renderSize - imageData 实际渲染尺寸。
 * @returns {string} imageData 渲染缓存 key。
 */
function getImageDataRenderKeyFromImageData(imageData, renderSize) {
  const sizeKey = renderSize
    ? `${renderSize.width}x${renderSize.height}`
    : 'auto';
  return `imageData:${hashString(stableStringify(imageData))}:size=${sizeKey}`;
}

/**
 * 根据图层 imageData 生成渲染缓存 key。
 * @param {Object} layer - 图层对象。
 * @returns {string} imageData 渲染缓存 key。
 */
export function getImageDataRenderKey(layer) {
  const imageData = parseMaterialImageData(layer && layer.imageData);
  return getImageDataRenderKeyFromImageData(
    imageData,
    getImageDataLayerRenderSize(imageData)
  );
}

/**
 * 判断图层正式 imageData 渲染缓存是否可直接使用。
 * @param {Object} layer - 图层对象。
 * @param {string} currentKey - 当前 imageData 对应的缓存 key。
 * @returns {boolean} 是否可直接使用正式缓存。
 */
function hasMatchedLayerRenderBlobUrl(layer, currentKey) {
  return !!(
    layer &&
    layer.imageDataRenderBlobUrl &&
    layer.imageDataRenderKey === currentKey
  );
}

/**
 * 判断图片地址是否可被浏览器加载。
 * @param {string} imageUrl - 图片地址。
 * @returns {Promise<boolean>} 图片是否可访问。
 */
function isImageUrlAccessible(imageUrl) {
  return new Promise((resolve) => {
    if (!imageUrl || typeof Image === 'undefined') {
      resolve(false);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = imageUrl;
  });
}

/**
 * 从 Fabric StaticCanvas 获取原生 canvas。
 * @param {fabric.StaticCanvas} offscreenCanvas - Fabric 离屏画布。
 * @returns {HTMLCanvasElement|null} 原生 canvas。
 */
function getNativeCanvasFromStaticCanvas(offscreenCanvas) {
  return (
    (offscreenCanvas && offscreenCanvas.lowerCanvasEl) ||
    (offscreenCanvas &&
      typeof offscreenCanvas.getElement === 'function' &&
      offscreenCanvas.getElement()) ||
    null
  );
}

/**
 * 把 dataURL 转换为 Blob。
 * @param {string} dataURL - dataURL 字符串。
 * @returns {Blob} 转换后的 Blob。
 */
function dataURLToBlob(dataURL) {
  const parts = String(dataURL || '').split(',');
  const meta = parts[0] || '';
  const content = parts[1] || '';
  const mimeMatch = meta.match(/data:([^;]+);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * 把离屏 Fabric Canvas 导出为 blobUrl。
 * @param {fabric.StaticCanvas} offscreenCanvas - Fabric 离屏画布。
 * @returns {Promise<string>} blobUrl；不支持 blobUrl 时回退 dataURL。
 */
function exportOffscreenCanvasToImageUrl(offscreenCanvas) {
  const canvasEl = getNativeCanvasFromStaticCanvas(offscreenCanvas);
  const createDataURL = () =>
    offscreenCanvas.toDataURL({
      format: 'png',
      quality: 1,
      enableRetinaScaling: false, // 关闭高清屏适配让图片和imageData 中宽高保持一致
      multiplier: 1
    });
  // 当前环境无法创建 blobUrl 时，回退 dataURL 保证绘制链路可用。
  if (
    !canvasEl ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return Promise.resolve(createDataURL());
  }
  // 优先使用原生 toBlob，避免 dataURL 到 Blob 的二次转换。
  if (typeof canvasEl.toBlob === 'function') {
    return new Promise((resolve, reject) => {
      canvasEl.toBlob((blob) => {
        // toBlob 失败时返回明确错误，方便进入外层降级逻辑。
        if (!blob) {
          reject(new Error('exportOffscreenCanvasToImageUrl: toBlob 结果为空'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      }, 'image/png');
    });
  }
  return Promise.resolve(URL.createObjectURL(dataURLToBlob(createDataURL())));
}

/**
 * 按 imageData 渲染尺寸比例同步缩放离屏画布内容。
 * @param {fabric.StaticCanvas} offscreenCanvas - Fabric 离屏画布。
 * @param {number} scale - imageData 渲染尺寸相对原始尺寸的比例。
 * @returns {void}
 */
function applyImageDataRenderViewportScale(offscreenCanvas, scale) {
  const renderScale = numberOr(scale, 1);
  // 1 倍渲染时保持 Fabric 默认视口，避免无意义更新。
  if (!offscreenCanvas || Math.abs(renderScale - 1) < 0.0001) {
    return;
  }
  const viewportTransform = [renderScale, 0, 0, renderScale, 0, 0];
  // Fabric 提供视口更新 API 时优先使用官方方法。
  if (typeof offscreenCanvas.setViewportTransform === 'function') {
    offscreenCanvas.setViewportTransform(viewportTransform);
    return;
  }
  offscreenCanvas.viewportTransform = viewportTransform;
}

/**
 * 获取离屏画布内所有对象的水平包围盒。
 * @param {fabric.StaticCanvas} offscreenCanvas - 离屏 Fabric 画布。
 * @returns {{minX:number,maxX:number}|null} 所有对象的水平包围盒。
 */
function getOffscreenCanvasObjectsHorizontalBounds(offscreenCanvas) {
  // 没有对象列表时无法计算水平包围盒。
  if (!offscreenCanvas || typeof offscreenCanvas.getObjects !== 'function') {
    return null;
  }
  const objects = offscreenCanvas.getObjects();
  let minX = Infinity;
  let maxX = -Infinity;
  objects.forEach((object) => {
    // 对象缺少包围盒 API 时跳过，避免影响其它有效对象。
    if (!object || typeof object.getBoundingRect !== 'function') {
      return;
    }
    object.setCoords();
    const rect = object.getBoundingRect();
    const left = Number(rect && rect.left);
    const width = Number(rect && rect.width);
    // 包围盒数据无效时跳过当前对象。
    if (!Number.isFinite(left) || !(width > 0)) {
      return;
    }
    minX = Math.min(minX, left);
    maxX = Math.max(maxX, left + width);
  });
  // 没有有效对象时不调整画布宽度。
  if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !(maxX > minX)) {
    return null;
  }
  return { minX, maxX };
}

/**
 * 获取 Fabric 对象列表内 IText 对象的自然文字宽度。
 * @param {Array<fabric.Object>} objects - 已创建且尚未分组变换的 Fabric 对象列表。
 * @returns {Object<string,{width:number}>} 以 IText 对象 id 为 key 的宽度信息。
 */
function getITextLayerInfoMapFromObjects(objects) {
  const textLayerInfoMap = {};
  const collectITextLayerInfo = (object) => {
    // 分组对象需要递归检查子对象，避免漏掉 group 内的 IText。
    if (object && typeof object.getObjects === 'function') {
      object.getObjects().forEach(collectITextLayerInfo);
      return;
    }
    // 只同步 imageData 中的 IText 对象宽度，图片不参与。
    if (
      !object ||
      !(object instanceof IText) ||
      !object.id ||
      !(Number(object.width) > 0)
    ) {
      return;
    }
    textLayerInfoMap[object.id] = {
      width: Math.max(1, Math.ceil(Number(object.width)))
    };
  };
  // 对象列表无效时无法计算 IText 宽度。
  if (!Array.isArray(objects)) {
    return textLayerInfoMap;
  }
  objects.forEach(collectITextLayerInfo);
  return textLayerInfoMap;
}

/**
 * 按画布内对象的水平包围盒扩展 imageData 渲染画布。
 * @param {fabric.StaticCanvas} offscreenCanvas - 离屏 Fabric 画布。
 * @param {Object|null} renderSize - imageData 渲染尺寸。
 * @param {number} width - 当前画布宽度。
 * @param {number} height - 当前画布高度。
 * @returns {{width:number,sourceWidth:number}} 调整后的画布宽度。
 */
function expandImageDataRenderCanvasWidthByContent(
  offscreenCanvas,
  renderSize,
  width,
  height
) {
  const renderScale = renderSize ? numberOr(renderSize.scale, 1) : 1;
  const bounds = getOffscreenCanvasObjectsHorizontalBounds(offscreenCanvas);
  // 没有有效包围盒时保持原画布宽度。
  if (!bounds) {
    return {
      width,
      sourceWidth: numberOr(renderSize && renderSize.sourceWidth, width)
    };
  }
  const shiftX = bounds.minX < 0 ? -bounds.minX : 0;
  const sourceWidth = numberOr(renderSize && renderSize.sourceWidth, width);
  const contentRight = Math.max(sourceWidth, bounds.maxX);
  const nextSourceWidth = Math.max(1, Math.ceil(contentRight + shiftX));
  const nextWidth = Math.max(width, Math.ceil(nextSourceWidth * renderScale));
  // 新宽度没有变大且内容未向左溢出时无需调整。
  if (nextWidth === width && !(shiftX > 0)) {
    return {
      width,
      sourceWidth
    };
  }
  // 内容向左溢出时整体右移，避免导出时左侧被裁掉。
  if (shiftX > 0 && typeof offscreenCanvas.getObjects === 'function') {
    offscreenCanvas.getObjects().forEach((object) => {
      if (!object || typeof object.set !== 'function') {
        return;
      }
      object.set('left', numberOr(object.left, 0) + shiftX);
      object.setCoords();
    });
  }
  setOffscreenStaticCanvasSize(offscreenCanvas, nextWidth, height);
  return {
    width: nextWidth,
    sourceWidth: nextSourceWidth
  };
}

/**
 * 解析素材 imageData，字符串和对象都支持。
 * @param {Object|string} imageData - 素材文字还原数据。
 * @returns {Object} 解析后的 imageData 对象。
 */
export function parseMaterialImageData(imageData) {
  if (!imageData) {
    throw new Error('parseMaterialImageData: imageData 不能为空');
  }
  if (typeof imageData === 'string') {
    return JSON.parse(imageData);
  }
  if (typeof imageData === 'object') {
    return JSON.parse(JSON.stringify(imageData));
  }
  throw new Error('parseMaterialImageData: imageData 类型无效');
}

/**
 * 判断素材图层是否需要从 imageData 合成新的 imageUrl。
 * @param {Object} layer - 图层对象。
 * @returns {boolean} 是否需要合成。
 */
function shouldResolveImageUrlFromImageData(layer) {
  if (!layer || layer.canvasType !== 'textbox' || !layer.imageData) {
    return false;
  }
  return true;
}

/**
 * 判断图层是否存在可用于绘制或派生的主图来源。
 * @param {Object} layer - 图层对象。
 * @returns {boolean} 是否存在主图来源。
 */
export function hasLayerImageSource(layer) {
  // 缺少图层对象时没有可用主图来源。
  if (!layer) {
    return false;
  }
  // 文字图层可以通过 imageData 临时还原出主图。
  if (shouldResolveImageUrlFromImageData(layer)) {
    return true;
  }
  return typeof layer.imageUrl === 'string' && !!layer.imageUrl;
}

/**
 * 规范化文字描边配置。
 * @param {Array<Object>} strokes - imageData 中的描边数组。
 * @returns {Array<Object>} 可用于 Fabric 渲染的描边数组。
 */
function importMaterialTextStrokes(strokes) {
  if (!Array.isArray(strokes)) {
    return [];
  }
  return strokes.map((stroke, index) => {
    const strokeColor = stroke.stroke || stroke.color || '#ffffff';
    return {
      id: stroke.id || `S${index + 1}`,
      color: strokeColor,
      width: numberOr(stroke.strokeWidth, numberOr(stroke.width, 0)),
      opacity: clampNumber(stroke.opacity, 0, 1),
      strokeLineJoin: stroke.strokeLineJoin || stroke.corner || 'round',
      strokeLineCap: stroke.strokeLineCap || 'butt',
      strokeMiterLimit: Math.max(1, numberOr(stroke.strokeMiterLimit, 4)),
      strokeDashArray: Array.isArray(stroke.strokeDashArray)
        ? stroke.strokeDashArray
        : []
    };
  });
}

/**
 * 安装多层描边渲染器。
 * @param {fabric.IText} object - 文字对象。
 * @returns {void}
 */
function installMaterialTextStrokeRenderer(object) {
  if (!object || object.__materialTextStrokeRenderer) {
    return;
  }
  const originalRenderStroke = object._renderTextStroke.bind(object);
  object._renderTextStroke = function renderMaterialTextStroke(ctx) {
    const strokes = (this.customStrokes || [])
      .filter((stroke) => numberOr(stroke.width, 0) > 0)
      .sort((a, b) => numberOr(b.width, 0) - numberOr(a.width, 0));
    if (!strokes.length) {
      return;
    }
    const previous = {
      stroke: this.stroke,
      strokeWidth: this.strokeWidth,
      strokeLineJoin: this.strokeLineJoin,
      strokeLineCap: this.strokeLineCap,
      strokeMiterLimit: this.strokeMiterLimit,
      strokeDashArray: this.strokeDashArray
    };
    try {
      strokes.forEach((stroke) => {
        ctx.save();
        try {
          this.stroke = stroke.color;
          this.strokeWidth = numberOr(stroke.width, 0);
          this.strokeLineJoin = stroke.strokeLineJoin;
          this.strokeLineCap = stroke.strokeLineCap;
          this.strokeMiterLimit = stroke.strokeMiterLimit;
          this.strokeDashArray = stroke.strokeDashArray;
          ctx.lineJoin = stroke.strokeLineJoin;
          ctx.lineCap = stroke.strokeLineCap;
          ctx.miterLimit = stroke.strokeMiterLimit;
          ctx.setLineDash && ctx.setLineDash(stroke.strokeDashArray);
          originalRenderStroke(ctx);
        } finally {
          ctx.restore();
        }
      });
    } finally {
      this.stroke = previous.stroke;
      this.strokeWidth = previous.strokeWidth;
      this.strokeLineJoin = previous.strokeLineJoin;
      this.strokeLineCap = previous.strokeLineCap;
      this.strokeMiterLimit = previous.strokeMiterLimit;
      this.strokeDashArray = previous.strokeDashArray;
    }
  };
  object.__materialTextStrokeRenderer = true;
}

/**
 * 将横排文字转换成竖排渲染文本。
 * @param {string} text - 原始文字。
 * @returns {string} 竖排渲染文本。
 */
function toMaterialVerticalText(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => splitMaterialGraphemes(line).join('\n'))
    .join('\n\n');
}

/**
 * 按字素拆分文本，避免竖排时拆散组合字符和 emoji。
 * @param {string} value - 待拆分文本。
 * @returns {Array<string>} 字素数组。
 */
function splitMaterialGraphemes(value) {
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: 'grapheme'
    });
    return [...segmenter.segment(String(value || ''))].map(
      (item) => item.segment
    );
  }
  return Array.from(String(value || ''));
}

/**
 * 按 imageData 的 layerInfo 恢复对象位置。
 * @param {fabric.Object} object - Fabric 对象。
 * @param {Object} layerInfo - imageData 内部图层位置信息。
 * @returns {void}
 */
function applyMaterialImageDataLayerInfo(object, layerInfo) {
  const info = layerInfo || {};
  const scale = Math.max(0.01, numberOr(info.scale, 1));
  const width = Math.max(0, numberOr(info.width, object.width || 0));
  const height = Math.max(0, numberOr(info.height, object.height || 0));
  const center = new Point(
    numberOr(info.left, 0) + (width * scale) / 2,
    numberOr(info.top, 0) + (height * scale) / 2
  );
  object.set({
    angle: numberOr(info.angle, 0),
    scaleX: scale,
    scaleY: scale,
    opacity: clampNumber(info.opacity, 0, 1),
    selectable: false,
    evented: false,
    objectCaching: false
  });
  object.setPositionByOrigin(center, 'center', 'center');
  object.setCoords();
}

/**
 * 按 IText 的 layerInfo 恢复文字位置。
 * @param {fabric.IText} object - Fabric 文字对象。
 * @param {Object} layerInfo - imageData 内部文字图层位置信息。
 * @returns {void}
 */
function applyMaterialITextLayerInfo(object, layerInfo) {
  const info = layerInfo || {};
  const scale = Math.max(0.01, numberOr(info.scale, 1));
  object.set({
    left: numberOr(info.left, 0),
    top: numberOr(info.top, 0),
    originX: 'left',
    originY: 'top',
    angle: numberOr(info.angle, 0),
    scaleX: scale,
    scaleY: scale,
    opacity: clampNumber(info.opacity, 0, 1),
    selectable: false,
    evented: false,
    objectCaching: false
  });
  object.setCoords();
}

/**
 * 从 imageData 对象描述创建 Fabric 对象。
 * @param {Object} item - imageData.objects 中的单个对象。
 * @returns {Promise<fabric.Object>} Fabric 对象。
 */
async function createMaterialObjectFromImageData(item) {
  if (item && item.type === IMAGE_DATA_TEXT_OBJECT_TYPE) {
    const style = (item.text && item.text.style) || {};
    const content = String((item.text && item.text.content) || '');
    const writingMode =
      style.writingMode === 'vertical-rl' ? 'vertical-rl' : 'horizontal-tb';
    const iText = new IText(
      writingMode === 'vertical-rl' ? toMaterialVerticalText(content) : content,
      {
        id: item.id,
        customTextContent: content,
        customWritingMode: writingMode,
        customStrokes: importMaterialTextStrokes(style.strokes || []),
        fontFamily: resolveImageDataTextFontFamily(item),
        fontSize: numberOr(style.fontSize, 28),
        fill: style.fill || '#111827',
        textAlign: style.textAlign || 'left',
        lineHeight: numberOr(style.lineHeight, 1.2),
        charSpacing: numberOr(style.charSpacing, 0),
        underline: !!style.underline,
        overline: !!style.overline,
        linethrough: !!style.linethrough,
        backgroundColor: style.backgroundColor || 'rgba(255, 255, 255, 0)',
        textBackgroundColor:
          style.textBackgroundColor || 'rgba(255, 255, 255, 0)',
        editable: false,
        selectable: false,
        evented: false,
        objectCaching: false
      }
    );
    installMaterialTextStrokeRenderer(iText);
    const firstStroke = iText.customStrokes[0];
    iText.set({
      stroke: firstStroke ? firstStroke.color : null,
      strokeWidth: firstStroke ? firstStroke.width : 0,
      strokeLineJoin: firstStroke ? firstStroke.strokeLineJoin : 'round',
      paintFirst: 'stroke'
    });
    if (style.shadow) {
      iText.set({
        shadow: {
          color: style.shadow.color || 'rgba(0,0,0,0)',
          blur: numberOr(style.shadow.blur, 0),
          offsetX: numberOr(style.shadow.offsetX, 0),
          offsetY: numberOr(style.shadow.offsetY, 0)
        }
      });
    }
    applyMaterialITextLayerInfo(iText, item.layerInfo);
    return iText;
  }
  if (item && item.type === 'image') {
    const image = await FabricImage.fromURL(item.imageUrl || '', {
      crossOrigin: 'anonymous'
    });
    applyMaterialImageDataLayerInfo(image, item.layerInfo);
    return image;
  }
  throw new Error(`暂不支持对象类型：${item && item.type}`);
}

/**
 * 根据 imageData 的分组描述恢复 Fabric 分组。
 * @param {Object} groupData - imageData.groups 中的分组对象。
 * @param {Array<fabric.Object>} children - 分组子对象。
 * @returns {fabric.Group} Fabric 分组对象。
 */
function createMaterialGroupFromImageData(groupData, children) {
  const absoluteTransforms = new Map(
    children.map((child) => [child, child.calcTransformMatrix()])
  );
  const group = new Group(children, {
    id: groupData.id,
    selectable: false,
    evented: false,
    objectCaching: false,
    subTargetCheck: true
  });
  const containerMatrix = group.calcTransformMatrix();
  const invertedContainerMatrix = util.invertTransform(containerMatrix);
  children.forEach((child) => {
    const absoluteMatrix = absoluteTransforms.get(child);
    if (!absoluteMatrix) {
      return;
    }
    util.applyTransformToObject(
      child,
      util.multiplyTransformMatrices(invertedContainerMatrix, absoluteMatrix)
    );
    child.setCoords();
  });
  applyMaterialImageDataLayerInfo(group, groupData.layerInfo);
  group.setCoords();
  return group;
}

/**
 * @l-doc:\doc\模板sku方案设计\modules\editImageNew\图层操作\文字素材绘制适配说明.md
 * 从文字 imageData 图层合成当前绘制使用的 imageUrl，并返回实际渲染尺寸。
 * @param {Object} layer - 图层对象。
 * @param {Object} [options] - 渲染配置。
 * @param {boolean} [options.useCache=true] - 是否允许复用已有缓存。
 * @returns {Promise<{imageUrl:string,imageDataRenderKey:string,width:number,height:number,sourceWidth:number,sourceHeight:number,scale:number,textLayerInfoMap:Object}>} 渲染结果。
 */
export async function resolveImageDataRenderResult(layer, options = {}) {
  // 非文字 imageData 图层继续使用原始 imageUrl。
  if (!shouldResolveImageUrlFromImageData(layer)) {
    return {
      imageUrl: layer && layer.imageUrl,
      imageDataRenderKey: '',
      width: 0,
      height: 0,
      sourceWidth: 0,
      sourceHeight: 0,
      scale: 1,
      textLayerInfoMap: {}
    };
  }
  const useCache = options.useCache !== false;
  let offscreenCanvas = null;
  try {
    const imageData = parseMaterialImageData(layer.imageData);
    const renderSize = getImageDataLayerRenderSize(imageData);
    const currentKey = getImageDataRenderKeyFromImageData(
      imageData,
      renderSize
    );
    // 正式缓存 key 匹配且 blobUrl 可访问时，优先使用图层上的 blobUrl。
    if (useCache && hasMatchedLayerRenderBlobUrl(layer, currentKey)) {
      // blobUrl 可能已被页面销毁逻辑释放，失效时继续向下重建。
      if (await isImageUrlAccessible(layer.imageDataRenderBlobUrl)) {
        return {
          imageUrl: layer.imageDataRenderBlobUrl,
          imageDataRenderKey: currentKey,
          width: renderSize ? renderSize.width : 0,
          height: renderSize ? renderSize.height : 0,
          sourceWidth: renderSize ? renderSize.sourceWidth : 0,
          sourceHeight: renderSize ? renderSize.sourceHeight : 0,
          scale: renderSize ? renderSize.scale : 1,
          textLayerInfoMap: {}
        };
      }
    }
    const cachedImageUrl = imageDataRenderUrlCache.get(currentKey);
    // 临时缓存命中且可访问时，直接复用本次 imageData 的还原结果。
    if (useCache && cachedImageUrl) {
      // 缓存里可能残留已 revoke 的 blobUrl，失效后删除并重新合成。
      if (await isImageUrlAccessible(cachedImageUrl)) {
        return {
          imageUrl: cachedImageUrl,
          imageDataRenderKey: currentKey,
          width: renderSize ? renderSize.width : 0,
          height: renderSize ? renderSize.height : 0,
          sourceWidth: renderSize ? renderSize.sourceWidth : 0,
          sourceHeight: renderSize ? renderSize.sourceHeight : 0,
          scale: renderSize ? renderSize.scale : 1,
          textLayerInfoMap: {}
        };
      }
      imageDataRenderUrlCache.delete(currentKey);
    }
    const layerInfo = (layer && layer.layerInfo) || {};
    const width = renderSize
      ? renderSize.width
      : Math.max(1, numberOr(imageData.width, numberOr(layerInfo.width, 1)));
    const height = renderSize
      ? renderSize.height
      : Math.max(1, numberOr(imageData.height, numberOr(layerInfo.height, 1)));
    await ensureImageDataFontsLoaded({ imageData });
    offscreenCanvas = createOffscreenStaticCanvas({ width, height });
    const objectMap = new Map();
    const topLevelObjects = [];
    for (let i = 0; i < (imageData.objects || []).length; i++) {
      const item = imageData.objects[i];
      const object = await createMaterialObjectFromImageData(item);
      objectMap.set(item.id, object);
      topLevelObjects.push(object);
    }
    const textLayerInfoMap = getITextLayerInfoMapFromObjects(topLevelObjects);
    const groupedIds = new Set();
    for (let i = 0; i < (imageData.groups || []).length; i++) {
      const groupData = imageData.groups[i];
      const children = (groupData.objects || [])
        .map((id) => objectMap.get(id))
        .filter(Boolean);
      children.forEach((child) => groupedIds.add(child.id));
      if (!children.length) {
        continue;
      }
      offscreenCanvas.add(
        createMaterialGroupFromImageData(groupData, children)
      );
    }
    topLevelObjects
      .filter((object) => !groupedIds.has(object.id))
      .forEach((object) => offscreenCanvas.add(object));
    const expandedSize = expandImageDataRenderCanvasWidthByContent(
      offscreenCanvas,
      renderSize,
      width,
      height
    );
    applyImageDataRenderViewportScale(
      offscreenCanvas,
      renderSize ? renderSize.scale : 1
    );
    offscreenCanvas.renderAll();
    const imageUrl = await exportOffscreenCanvasToImageUrl(offscreenCanvas);
    // 普通绘制场景允许缓存，文字编辑尺寸测量场景会跳过缓存。
    if (useCache) {
      imageDataRenderUrlCache.set(currentKey, imageUrl);
    }
    return {
      imageUrl,
      imageDataRenderKey: currentKey,
      width: expandedSize.width,
      height,
      sourceWidth: expandedSize.sourceWidth,
      sourceHeight: renderSize
        ? renderSize.sourceHeight
        : numberOr(imageData.height, numberOr(layerInfo.height, height)),
      scale: renderSize ? renderSize.scale : 1,
      textLayerInfoMap
    };
  } catch (error) {
    console.error('resolveImageDataRenderResult: imageData 解析失败', error);
    // 字体文件缺失不能静默回退，否则导出会使用系统字体。
    if (error && error.message === IMAGE_DATA_FONT_NOT_FOUND_MESSAGE) {
      Toast(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
      throw error;
    }
    // 开发环境抛出错误，便于及时发现 imageData 结构问题。
    if (mwx.isDev) {
      Toast('文字素材解析失败');
      throw error;
    }
    return {
      imageUrl: layer && layer.imageUrl,
      imageDataRenderKey: '',
      width: 0,
      height: 0,
      sourceWidth: 0,
      sourceHeight: 0,
      scale: 1,
      textLayerInfoMap: {}
    };
  } finally {
    // 离屏 Fabric Canvas 使用完毕后立即释放。
    if (offscreenCanvas) {
      try {
        offscreenCanvas.dispose();
      } catch (error) {
        /* noop */
      }
    }
  }
}

/**
 * @l-doc:\doc\模板sku方案设计\modules\editImageNew\图层操作\文字素材绘制适配说明.md
 * 从文字 imageData 图层合成当前绘制使用的 imageUrl。
 * @param {Object} layer - 图层对象。
 * @returns {Promise<string>} 合成后的 blobUrl；无需合成或失败回退时返回 layer.imageUrl。
 */
export async function resolveImageUrlFromImageData(layer) {
  const result = await resolveImageDataRenderResult(layer);
  return result.imageUrl;
}

/**
 * 获取图层当前绘制或派生使用的主图地址。
 * @param {Object} layer - 图层对象。
 * @param {Object} [options] - 解析配置。
 * @param {boolean} [options.ignoreStampingEffectImage=false] - 是否忽略烫色模板/素材最终效果图并解析原始图。
 * @returns {Promise<string>} 当前应使用的图片地址。
 */
export async function resolveLayerImageUrl(layer, options = {}) {
  const { ignoreStampingEffectImage = false } = options;
  // 绘制烫色模板/素材最终效果时，优先使用已生成的烫色效果图。
  if (
    !ignoreStampingEffectImage &&
    layer &&
    (layer.type === constant.layer_stampingTemplate ||
      layer.type === constant.layer_stampingMaterial) &&
    layer.stampingEffectImageUrl
  ) {
    return layer.stampingEffectImageUrl;
  }
  // 没有可用主图来源时返回空字符串，由调用方决定跳过或报错。
  if (!hasLayerImageSource(layer)) {
    return '';
  }
  return await resolveImageUrlFromImageData(layer);
}
